import React, { useCallback, useMemo } from "react";
import { Box, Chip, Stack, Tooltip } from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import DateRangePickerDialog from "../../../../Shared/datePicker";
import {
  dashboardChartRangesEqual,
  presetLastSixMonths,
  presetLastThreeMonths,
  presetLastYear,
  presetThisMonth,
} from "../utils/dashboardChartDateRange";
import { useDashboardChartsDate } from "../context/dashboardChartsDateContext";
import {
  activePresetChipSx,
  filterChipSx,
  iconActionChipSx,
  outlinedFilterChipSx,
  periodSelectChipSx,
} from "./chartFilterStyles";

const PRESET_DEFS = [
  { id: "m", label: "Questo mese", getRange: presetThisMonth },
  { id: "3m", label: "Ultimi 3 mesi", getRange: presetLastThreeMonths },
  { id: "6m", label: "Ultimi 6 mesi", getRange: presetLastSixMonths },
  { id: "1y", label: "Ultimo anno", getRange: presetLastYear },
];

export default function ChartDateFilterBar() {
  const {
    customRange,
    setCustomRange,
    clearCustomRange,
    dialogOpen,
    dialogAnchorEl,
    openDialog,
    closeDialog,
  } = useDashboardChartsDate();

  const applyPreset = useCallback(
    (getRange) => {
      setCustomRange(getRange());
    },
    [setCustomRange]
  );

  const presetActiveFlags = useMemo(
    () => PRESET_DEFS.map((p) => dashboardChartRangesEqual(customRange, p.getRange())),
    [customRange]
  );

  const hasCustom = Boolean(customRange?.start && customRange?.end);

  const handleOpenPeriodPicker = useCallback(
    (event) => {
      openDialog(event.currentTarget);
    },
    [openDialog]
  );

  return (
    <>
      <DateRangePickerDialog
        open={dialogOpen}
        onClose={closeDialog}
        anchorEl={dialogAnchorEl}
        initialStart={customRange?.start ?? null}
        initialEnd={customRange?.end ?? null}
        showQuickPresets={false}
        onApply={({ start, end }) => {
          if (!start || !end) {
            clearCustomRange();
          } else {
            setCustomRange({ start, end });
          }
        }}
        title="Seleziona periodo"
      />

      <Stack
        direction="row"
        useFlexGap
        spacing={1}
        flexWrap="wrap"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ minWidth: 0 }}
      >
        {PRESET_DEFS.map((p, idx) => {
          const active = presetActiveFlags[idx];
          return (
            <Chip
              key={p.id}
              label={p.label}
              size="small"
              onClick={() => applyPreset(p.getRange)}
              variant="outlined"
              sx={active ? activePresetChipSx : { ...filterChipSx, ...outlinedFilterChipSx }}
            />
          );
        })}

        {!hasCustom ? (
          <Chip
            label={
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                Seleziona periodo
                <KeyboardArrowDown
                  aria-hidden
                  sx={{
                    fontSize: 18,
                    color: "#fff",
                    transition: "transform 0.2s ease",
                    transform: dialogOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </Box>
            }
            size="small"
            onClick={handleOpenPeriodPicker}
            aria-expanded={dialogOpen}
            aria-haspopup="dialog"
            sx={{ ...periodSelectChipSx, "& .MuiChip-label": { px: 1 } }}
          />
        ) : null}

        {hasCustom ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Chip
              label={
                <span>
                  <span style={{ color: "#095407" }}>{customRange.start.format("DD/MM/YYYY")}</span>
                  <span style={{ color: "#666666" }}> – </span>
                  <span style={{ color: "#095407" }}>{customRange.end.format("DD/MM/YYYY")}</span>
                </span>
              }
              size="small"
              variant="outlined"
              sx={{
                ...outlinedFilterChipSx,
                maxWidth: 280,
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
              }}
            />

            <Tooltip title="Modifica il periodo selezionato" arrow>
              <Chip
                component="button"
                type="button"
                aria-label="Modifica periodo"
                variant="outlined"
                size="small"
                label={<EditOutlined sx={{ fontSize: 18, color: "#666666", display: "block" }} />}
                onClick={handleOpenPeriodPicker}
                sx={iconActionChipSx}
              />
            </Tooltip>
            <Tooltip title="Rimuovi il filtro e usa il periodo predefinito per ogni grafico" arrow>
              <Chip
                component="button"
                type="button"
                aria-label="Rimuovi filtro date"
                variant="outlined"
                size="small"
                label={<CloseRounded sx={{ fontSize: 18, color: "#666666", display: "block" }} />}
                onClick={clearCustomRange}
                sx={iconActionChipSx}
              />
            </Tooltip>
          </Box>
        ) : null}
      </Stack>
    </>
  );
}
